import { Component, ErrorHandler, Inject, OnInit, ViewChild, ChangeDetectionStrategy, signal } from '@angular/core'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'

import { CurrencyService } from '@lemon/core/service/currency.service'
import { DateService } from '@lemon/core/service/date.service'
import { AllowedDialogsService } from 'app/_angular/service/allowed-dialogs.service'
import { FormValidationService } from 'app/_jaettu-angular/service/form-validation.service'
import { LadataanService } from 'app/_jaettu-angular/service/ladataan.service'
import { LemonTranslationService } from 'app/_jaettu-angular/service/lemon-translation.service'
import { WindowSizeService } from 'app/_jaettu-angular/service/window.service'

import { MatStepper } from '@angular/material/stepper'
import {
  PalvelukuvauksenPdfRequest,
  PalvelukuvauksenPdfResponse,
  SOPIMUS_LIITETYYPIT,
  SOPIMUS_LIITE_NIMET,
  SopimuksenHyvaksynnanRequest,
  SopimuksenHyvaksynnanResponse,
  SopimusLiiteAsiakkaanHakuPyynto,
  SopimusLiiteAsiakkaanHakuVastaus,
  SopimusLiitetyyppi,
  SopimusPdfRequest,
  SopimusPdfResponse,
} from '@lemon/core-firebase/model/sopimus'
import { Observable, Subject, combineLatest } from 'rxjs'
import { map, takeUntil } from 'rxjs/operators'

import { LemonaidAsiakkaanKuukausihinta, LemonaidAsiakkaanSopimuskausi, SopimusLisapalvelu } from '@lemon/core-firebase/model/kayttaja'
import { tuotenimikkeet } from '@lemon/core-firebase/model/tuotenimikkeet'
import { AsiakasJaettuLemonaidService } from '@lemon/core-firebase/service/asiakas-jaettu-lemonaid.service'
import { LocalDate } from '@lemon/core/model/common'
import { FirebaseLemonaid } from 'app/_angular/service/firebase-lemonaid.service'
import { KayttajaService } from 'app/_angular/service/kayttaja.service'
import { FileSaverService } from 'app/_jaettu-angular/service/file-saver'
import { PalvelukokonaisuusService } from 'app/_jaettu-angular/service/palvelukokonaisuus.service'
import { TiedostojenLataamisService } from 'app/_jaettu-angular/service/tiedostojen-lataamis.service'
import { MuutoksiaPalveluunDialogService } from '../_angular/service/muutoksia-palveluun-dialog.service'

export interface MuutoksiaPalveluunDialogData {
  vanhaVersio: LemonaidAsiakkaanSopimuskausi['edellinen']
  uusiVersio: LemonaidAsiakkaanSopimuskausi
}

interface VeloituksetViewable {
  nimi: string
  hinta: number
  frekvenssi: string
  tuleeVoimaan?: LocalDate
}

@Component({
  templateUrl: './muutoksia-palveluun.dialog.html',
  styleUrls: ['./muutoksia-palveluun.dialog.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class MuutoksiaPalveluunDialog implements OnInit {
  @ViewChild('horizStepper', { read: MatStepper }) horizStepper: MatStepper
  @ViewChild('verticStepper', { read: MatStepper }) verticStepper: MatStepper

  commonError: string
  readonly cellphone = signal(true)
  readonly liitetyypit = SOPIMUS_LIITETYYPIT
  readonly liiteNimet = SOPIMUS_LIITE_NIMET
  readonly hyvaksytty = signal(false)
  kuukausihinta: string
  sopimuksenLoppupaiva: string
  hintaEiMuutu: boolean

  readonly vanhaPaketti = signal<{ nimi: string; palvelut: string[] }>(undefined)
  readonly uusiPaketti = signal<{ nimi: string; palvelut: string[] }>(undefined)

  private _ngUnsubscribe = new Subject<void>()

  public sopimusLisapalvelu = SopimusLisapalvelu
  onLisapalveluita = false

  nykyinenJaSeuraavaHintaObservable: Observable<{ nykyinen: LemonaidAsiakkaanKuukausihinta; seuraava: LemonaidAsiakkaanKuukausihinta }>
  muutJatkuvatVeloituksetObservable: Observable<VeloituksetViewable[]>

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: MuutoksiaPalveluunDialogData,
    private _dialogRef: MatDialogRef<MuutoksiaPalveluunDialog>,
    private _ladataanService: LadataanService,
    private _errorHandler: ErrorHandler,
    private _windowSizeService: WindowSizeService,
    private _allowedDialogsService: AllowedDialogsService,
    private _translationService: LemonTranslationService,
    private _validationService: FormValidationService,
    private _muutoksiaPalveluunDialogService: MuutoksiaPalveluunDialogService,
    private _currencyService: CurrencyService,
    private _dateService: DateService,
    private _firebaseLemonaid: FirebaseLemonaid,
    private _fileSaverService: FileSaverService,
    private _tiedostojenLataamisService: TiedostojenLataamisService,
    private _palvelukokonaisuusService: PalvelukokonaisuusService,
    private _kayttajaService: KayttajaService,
    private _asiakasJaettuService: AsiakasJaettuLemonaidService,
  ) {
    _dialogRef.disableClose = true
  }

  ngOnInit() {
    this.onLisapalveluita = this._onkoTaiOlikoLisapalveluita()

    // Preheat
    this._firebaseLemonaid.functionsCall<string, void>('sopimusHyvaksynnanTallennus', '')

    this._dialogRef
      .beforeClosed()
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe(() => {
        this._allowedDialogsService.allowAll()
      })

    this.cellphone.set(window.innerWidth < 670 || window.innerHeight < 670)
    this._windowSizeService.sizeObservable.pipe(takeUntil(this._ngUnsubscribe)).subscribe(() => {
      this.cellphone.set(window.innerWidth < 670 || window.innerHeight < 670)
    })

    this._translationService.currentLanguageObservable.pipe(takeUntil(this._ngUnsubscribe)).subscribe(kieli => {
      if (kieli) {
        if (this.data.vanhaVersio && this.data.vanhaVersio?.palvelupaketti !== this.data.uusiVersio.palvelupaketti) {
          this.vanhaPaketti.set({
            nimi: this._palvelukokonaisuusService.annaPalvelupaketinNimi(this.data.vanhaVersio.palvelupaketti, kieli),
            palvelut: this._palvelukokonaisuusService.annaPalvelukokonaisuus(this.data.vanhaVersio.palvelupaketti, kieli),
          })
        } else {
          this.vanhaPaketti.set(null)
        }

        this.uusiPaketti.set({
          nimi: this._palvelukokonaisuusService.annaPalvelupaketinNimi(this.data.uusiVersio.palvelupaketti, kieli),
          palvelut: this._palvelukokonaisuusService.annaPalvelukokonaisuus(this.data.uusiVersio.palvelupaketti, kieli),
        })
      }
    })

    this.nykyinenJaSeuraavaHintaObservable = this._kayttajaService.nykyisenAsiakkaanHinnatObservable.pipe(
      map(hinnat => {
        if (!hinnat?.length) {
          return { nykyinen: null, seuraava: null }
        }
        const currentNumberDate = this._dateService.currentNumberDate()
        const sorted = [...hinnat].sort((a, b) => a.voimassaAlkaen - b.voimassaAlkaen)
        const nykyisenIndeksi = sorted.findIndex(hinta => hinta.voimassaAlkaen <= currentNumberDate && hinta.voimassaLoppuen >= currentNumberDate)
        if (nykyisenIndeksi > -1) {
          const nykyinen = sorted[nykyisenIndeksi]
          const seuraava = nykyisenIndeksi < sorted.length - 1 ? sorted[nykyisenIndeksi + 1] : null
          return { nykyinen: nykyinen, seuraava: seuraava }
        }
        const seuraavaTulevaHinta = sorted.find(hinta => hinta.voimassaAlkaen >= currentNumberDate)
        if (seuraavaTulevaHinta) {
          return { nykyinen: null, seuraava: seuraavaTulevaHinta }
        }

        // No prices found
        return null
      }),
    )

    this.muutJatkuvatVeloituksetObservable = combineLatest([this._kayttajaService.muutJatkuvatVeloituksetObservable, this._translationService.currentLanguageObservable]).pipe(
      map(([veloitukset, kieli]) => {
        const currentLocalDate = this._dateService.currentLocalDate()
        const { nykyiset, tulevat } = this._asiakasJaettuService.annaNykyisetJaVoimaanTulevatMuutJatkuvatVeloitukset(veloitukset, currentLocalDate)
        return [...nykyiset, ...tulevat].map(v => {
          const tuotenimike = tuotenimikkeet.find(t => t.avain === v.tuote)
          const tuoteNimi = kieli === 'en' ? tuotenimike.engNimi : tuotenimike.nimi
          const frekvenssiText = this._translationService.lokalisoiKielella('muutoksia-palveluun.' + v.frekvenssi, kieli)

          const veloitus: VeloituksetViewable = {
            nimi: tuoteNimi,
            hinta: v.hinta,
            frekvenssi: frekvenssiText,
          }

          if (this._dateService.compareLocalDates(v.voimassaAlkaen, '>', currentLocalDate)) {
            veloitus.tuleeVoimaan = v.voimassaAlkaen
          }
          return veloitus
        })
      }),
    )
  }

  hyvaksyJaJatka() {
    // Show loading indicator
    this._ladataanService.aloitaLataaminen()

    const req: SopimuksenHyvaksynnanRequest = {
      kieli: this._translationService.nykyinenKieli,
      asiakkaanSopimusversionAvain: this.data.uusiVersio.sopimusversioAvain,
    }

    // Call CF
    return this._firebaseLemonaid
      .functionsCall<SopimuksenHyvaksynnanRequest, SopimuksenHyvaksynnanResponse>('sopimusHyvaksynnanTallennus', req)
      .then(response => {
        // If no response, or response contains an error
        if (!response || response.e) {
          this.commonError = this._translationService.lokalisoi('yleiset.tuntematon-virhe', this._translationService.nykyinenKieli)
          this._errorHandler.handleError(response ? response.e : 'no-response')
        } else {
          this.hyvaksytty.set(true)
        }
      })
      .catch(error => {
        this.commonError = this._translationService.lokalisoi('yleiset.tuntematon-virhe', this._translationService.nykyinenKieli)
        this._errorHandler.handleError(error)
      })
      .finally(() => {
        // Stop loading indicator
        this._ladataanService.lopetaLataaminen()
      })
  }

  postpone() {
    this._muutoksiaPalveluunDialogService.postpone()
    this._dialogRef.close()
  }

  async downloadSopimusPreview() {
    this._ladataanService.aloitaLataaminen()

    const req: SopimusPdfRequest = {
      versio: 'uusin',
      kieli: this._translationService.nykyinenKieli || 'fi',
    }
    return this._firebaseLemonaid
      .functionsCall<SopimusPdfRequest, SopimusPdfResponse>('sopimusAnnaPdfPreview', req)
      .then(response => {
        if (!response || response.e || !response.base64String) {
          this.commonError = this._translationService.lokalisoi('yleiset.tuntematon-virhe', this._translationService.nykyinenKieli)
          this._errorHandler.handleError(response ? response.e : 'no-response')
        } else {
          this._downloadSopimusPdf(response.base64String)
        }
      })
      .catch(err => this._errorHandler.handleError(err))
      .finally(() => this._ladataanService.lopetaLataaminen())
  }

  private _downloadSopimusPdf(base64File: string) {
    const uint8Array = this._tiedostojenLataamisService.base64ToUint8Array(base64File)
    const fileName = 'Tilitoimisto Lemon Tree Oy Toimeksiantosopimus.pdf'
    this._fileSaverService.saveAs(new Blob([uint8Array], { type: 'application/pdf' }), fileName)
  }

  async downloadLiite(liitetyyppi: SopimusLiitetyyppi) {
    this._ladataanService.aloitaLataaminen()
    const req: SopimusLiiteAsiakkaanHakuPyynto = { liitetyyppi, kieli: this._translationService.nykyinenKieli || 'fi' }
    return this._firebaseLemonaid
      .functionsCall<SopimusLiiteAsiakkaanHakuPyynto, SopimusLiiteAsiakkaanHakuVastaus>('sopimusAnnaLiite', req)
      .then(response => {
        if (!response || response.e || !response.base64String) {
          this.commonError = this._translationService.lokalisoi('yleiset.tuntematon-virhe', this._translationService.nykyinenKieli)
          this._errorHandler.handleError(response ? response.e : 'no-response')
        } else {
          const uint8Array = this._tiedostojenLataamisService.base64ToUint8Array(response.base64String)
          const nimi = this._translationService.lokalisoi(this.liiteNimet[liitetyyppi], this._translationService.nykyinenKieli || 'fi')
          this._fileSaverService.saveAs(new Blob([uint8Array], { type: 'application/pdf' }), nimi + '.pdf')
        }
      })
      .catch(err => this._errorHandler.handleError(err))
      .finally(() => this._ladataanService.lopetaLataaminen())
  }

  async downloadPalvelukuvaus() {
    this._ladataanService.aloitaLataaminen()

    const req: PalvelukuvauksenPdfRequest = {
      kieli: this._translationService.nykyinenKieli || 'fi',
    }
    return this._firebaseLemonaid
      .functionsCall<PalvelukuvauksenPdfRequest, PalvelukuvauksenPdfResponse>('sopimusAnnaPalvelukuvausPdf', req)
      .then(response => {
        if (!response || response.e || !response.base64String) {
          this.commonError = this._translationService.lokalisoi('yleiset.tuntematon-virhe', this._translationService.nykyinenKieli)
          this._errorHandler.handleError(response ? response.e : 'no-response')
        } else {
          this._downloadPalvelukuvausPdf(response.base64String)
        }
      })
      .catch(err => this._errorHandler.handleError(err))
      .finally(() => this._ladataanService.lopetaLataaminen())
  }

  private _downloadPalvelukuvausPdf(base64File: string) {
    const uint8Array = this._tiedostojenLataamisService.base64ToUint8Array(base64File)
    const fileName = 'Tilitoimisto Lemon Tree Oy Palvelukuvaus.pdf'
    this._fileSaverService.saveAs(new Blob([uint8Array], { type: 'application/pdf' }), fileName)
  }

  sulje() {
    this._dialogRef.close()
  }

  private _onkoTaiOlikoLisapalveluita() {
    return Object.values(this.data.vanhaVersio?.lisapalvelut || {}).filter(value => value).length > 0 || Object.values(this.data.uusiVersio?.lisapalvelut || {}).filter(value => value).length > 0
  }
}
